import { useEffect, useState } from 'react';

import { MtxGrid } from '@meilleurtaux/mtp-uikit-react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { ChartResponse, getGetApiPlacementDefiscalisationQueryKey, useGetApiPlacementDefiscalisation } from '@/api';
import ArbitrageProposalBanner from '@/components/ArbitrageProposalBanner/ArbitrageProposalBanner';
import Breadcrumb from '@/components/Breadcrumb/Breadcrumb';
import ChipsRadioSmall from '@/components/chipsRadioSmall/chipsRadioSmall';
import ContractDetailsHeader from '@/components/ContractDetailsHeader/ContractDetailsHeader';
import { ContractDocuments } from '@/components/ContractDocuments/ContractDocuments';
import DemoTag from '@/components/DemoTag/DemoTag';
import LineChart from '@/components/LineChart/LineChart';
import { dataButtonFiltre } from '@/components/LineChart/lineChartButtons';
import { Loader } from '@/components/Loader/loader';
import ModaleDefaut from '@/components/ModaleDefaut/ModaleDefaut';
import Tabs from '@/components/Tabs/Tabs';
import TaxExemptionDetails from '@/components/TaxExemptionDetails/TaxExemptionDetails';
import { TrendingValue } from '@/components/TrendingValue/TrendingValue';
import { BREAKPOINTS } from '@/constants/breakpoints';
import { FEATURE_FLAGS } from '@/constants/featureFlags';
import { ROUTE } from '@/constants/route';
import { TIMESPAN } from '@/enums/timespan';
import { useDemoContext } from '@/hooks/DemoContext';
import { useArbitrages } from '@/hooks/useArbitrages';
import { useAuth } from '@/hooks/useAuth';
import useCancelModal from '@/hooks/useCancelModal';
import { useFilteredDocuments } from '@/hooks/useFilteredDocuments';
import useScreenSize from '@/hooks/useScreenSize';
import { TRACKING_INSTANCE } from '@/hooks/useTracking/useTrackingInit/useTrackingInit';
import ContentModalCalculPerformance from '@/modals/ContentModalCalculPerformance/ContentModalCalculPerformance';
import { PopinDefisc } from '@/modals/ContentModalCalculPerformance/data';
import ContentModalRenonciation from '@/modals/ContentModalRenonciation/ContentModalRenonciation';
import { BreadcrumbItem } from '@/services/types/Breadcrumb';
import { performanceFilter } from '@/utils/functions/performanceFilter/performanceFilter';

import styles from './styles.module.css';

export const DefiscDetails: React.FC = () => {
  const { t } = useTranslation();
  const { windowWidth } = useScreenSize();
  const { user } = useAuth();
  const { isDemo } = useDemoContext();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams<{ id: string }>();
  const [lineChartTimespan, setLineChartTimespan] = useState<TIMESPAN>(TIMESPAN.allTime);
  const { data, isLoading, isError } = useGetApiPlacementDefiscalisation(id ?? '');
  const [showModalCalculPerf, setShowModalCalculPerf] = useState<boolean>(false);
  const { showCancelModal, openCancelModal, closeCancelModal } = useCancelModal();
  const [performance, setPerformance] = useState<number | null>(0);
  const [plusMoinsValue, setPlusMoinsValue] = useState<number | null>(0);

  const { data: arbitrageProposals, isLoading: isArbitrageProposalsLoading } = useArbitrages(
    { idPlacement: data?.placementId ?? undefined },
    { query: { enabled: !!data?.placementId } }
  );

  const { filterPerformanceInvestment } = performanceFilter(data as ChartResponse);

  const openModal = (modal: string) => {
    document.body.classList.add('modal-open');

    if (modal === 'showModalCalculPerf') {
      setShowModalCalculPerf(true);
    }
  };

  const closeModal = () => {
    document.body.classList.remove('modal-open');
    setShowModalCalculPerf(false);
  };

  const breadcrumbItems: BreadcrumbItem[] = [
    { label: 'Mes contrats', path: ROUTE.CONTRATS },
    { label: data?.nomProduit, path: ROUTE.DEFISCALISATION },
  ];

  useEffect(() => {
    if (data && user?.id && user.type && user.success) {
      TRACKING_INSTANCE.setTcVars({
        page_nom: `mes contrats - ${data?.nomProduit}`,
        page_template: 'espace client',
        user_id: user.id,
        user_client: user.type === 'Client',
        user_connexion: user?.success,
        user_statut_contrat: sessionStorage.getItem('user_statut_contrat'),
      });
      void TRACKING_INSTANCE.triggerEvent('page', undefined, {
        event_id: 'page',
        event_categorie: 'affichage espace client',
        event_nom: `mes contrats - ${data?.nomProduit} - contrat`,
        event_page: `mes contrats - ${data?.nomProduit} - contrat`,
      });
      filterPerformanceInvestment({ lineChartTimespan, setPerformance, setPlusMoinsValue });
    }
  }, [data, lineChartTimespan, filterPerformanceInvestment, user?.id, user?.type, user?.success]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);
  const { exPostDocuments, regularDocuments } = useFilteredDocuments({
    documents: data?.documents,
  });

  const valorisationEncours = data?.valorisation?.dateEncours
    ? data?.valorisation?.dateEncours.split('-').reverse().join('/')
    : '--/--/----';
  if (isError) navigate(ROUTE.ERREUR, { state: { goTo: ROUTE.CONTRATS } });
  if (!data) return <Loader />;

  return (
    <>
      {isLoading && <Loader />}
      <div className={styles.contractDetailsContainer}>
        {showModalCalculPerf && (
          <ModaleDefaut
            content={ContentModalCalculPerformance({ ...PopinDefisc.args })}
            btnLabel="J'ai compris"
            btnVariant="primary"
            btnSize="large"
            btnFullwidth={false}
            onClickBtn={() => closeModal()}
            onClickClose={() => closeModal()}
            mobileStyle="defaut"
            size="large"
          />
        )}

        {showCancelModal && (
          <ContentModalRenonciation
            nomProduit={data?.nomProduit ?? ''}
            placementId={data?.placementId ?? ''}
            delayDays={data?.cancelablePeriod ?? 14}
            onClose={closeCancelModal}
            onSuccess={() => {
              queryClient.invalidateQueries({ queryKey: getGetApiPlacementDefiscalisationQueryKey(id ?? '') });
              closeCancelModal();
            }}
          />
        )}

        <div>
          <Breadcrumb items={breadcrumbItems} />
        </div>
        <div>
          <div>
            <ContractDetailsHeader
              nomProduit={data?.nomProduit}
              isCancelable={data?.isCancelable}
              cancelablePeriod={data?.cancelablePeriod ?? 14}
              manager={data?.gestionnaire}
              openCancelModal={openCancelModal}
            />

            <div className={styles.contractDetailsValuationAmountDate}>
              <div className={styles.taxExemptionValuation}>
                <span className={styles.valuation}>
                  {t('format.currencyFlexible', { value: data?.valorisation?.encours ?? 0 })}
                </span>
                <button
                  className={styles.contractDetailsValuationInfo}
                  onClick={() => {
                    openModal('showModalCalculPerf');
                    void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
                      event_id: 'clic',
                      event_categorie: 'clic',
                      event_metier: 'espace client placement',
                      event_nom: 'info defisc',
                      event_detail_1: 'picto',
                    });
                  }}
                >
                  <i className="totem-icon totem-icon-info" />
                </button>
                {isDemo && <DemoTag />}
              </div>

              <p className={styles.contractDetailsValuationDate}>
                {t('taxExemption.description')} {valorisationEncours}
              </p>
            </div>

            <div className={styles.contractDetailsPerformanceContainer}>
              <div className={styles.contractDetailsPerformance}>
                <p className={styles.contractDetailsPerformanceTitle}>
                  {t('taxExemption.performanceWithoutTaxBenefit')}
                </p>
                <TrendingValue value={plusMoinsValue} secondaryValue={performance} icon={plusMoinsValue !== null} />
              </div>
            </div>
          </div>

          <div>
            <LineChart timespan={lineChartTimespan} donnees={data?.chartData ?? []} />
          </div>

          <div className={styles.filterButtonContent}>
            {dataButtonFiltre.map((value, index, buttonsFiltre) => (
              <ChipsRadioSmall
                key={value.value}
                label={windowWidth <= BREAKPOINTS.mobile ? value.textMobile : value.text}
                name="lineChartButtons"
                value={value.value}
                defaultCheck={index + 1 === buttonsFiltre.length}
                onClick={() => {
                  setLineChartTimespan(value.value);
                  filterPerformanceInvestment({ lineChartTimespan: value.value, setPerformance, setPlusMoinsValue });
                  void TRACKING_INSTANCE.triggerEvent('clic', undefined, value.tracking);
                }}
              />
            ))}
          </div>
        </div>

        {FEATURE_FLAGS.ARBITRAGE &&
          arbitrageProposals &&
          !isArbitrageProposalsLoading &&
          windowWidth <= BREAKPOINTS.mobile && <ArbitrageProposalBanner proposals={arbitrageProposals} />}

        <Tabs
          tabs={[
            {
              label: 'Contrat',
              disabled: false,
              onClick: () => {
                void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
                  event_id: 'clic',
                  event_categorie: 'clic',
                  event_metier: 'espace client placement',
                  event_nom: 'contrat',
                  event_detail_1: 'onglet',
                });
              },
              content: (
                <div className={styles.contractDetailsSection}>
                  <h2 className={styles.contractDetailsSectionTitle}>
                    {t('taxExemption.title')}
                    {isDemo && <DemoTag />}
                  </h2>

                  <MtxGrid row>
                    <MtxGrid col={7} colXs={12} colSm={12} colMd={12} colLg={7}>
                      <TaxExemptionDetails data={data} />
                    </MtxGrid>
                  </MtxGrid>
                </div>
              ),
            },
            {
              label: 'Documents',
              disabled: data?.documents === null,
              onClick: () => {
                void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
                  event_id: 'clic',
                  event_categorie: 'clic',
                  event_metier: 'espace client placement',
                  event_nom: 'documents',
                  event_detail_1: 'onglet',
                });
              },
              content: (
                <div className={styles.contractDetailsBody}>
                  <div className={styles.contractDetailsSectionContainer}>
                    <div className={styles.contractDetailsSection}>
                      <h2 className={styles.contractDetailsSectionTitle}>
                        {t('documents.regularReport', { count: regularDocuments.length })}
                        {isDemo && <DemoTag />}
                      </h2>

                      <MtxGrid row>
                        <MtxGrid col={7} colXs={12} colSm={12} colMd={12} colLg={7}>
                          <ContractDocuments documentsInfos={regularDocuments} />
                        </MtxGrid>
                      </MtxGrid>
                    </div>
                    {exPostDocuments.length > 0 && (
                      <div className={styles.contractDetailsSection}>
                        <h2>{t('documents.exPostReport', { count: exPostDocuments.length })}</h2>

                        <MtxGrid row>
                          <MtxGrid col={7} colXs={12} colSm={12} colMd={12} colLg={7}>
                            <ContractDocuments documentsInfos={exPostDocuments} />
                          </MtxGrid>
                        </MtxGrid>
                      </div>
                    )}
                  </div>
                </div>
              ),
            },
          ]}
        />
      </div>
    </>
  );
};
